import { Button, type ButtonName } from "../worker/protocol";

export type Action = ButtonName | "FAST_FORWARD";
export type KeyMap = Record<Action, string>;

/** Keyboard defaults (§7.2), by `KeyboardEvent.code`. */
export const DEFAULT_KEYMAP: KeyMap = {
  UP: "KeyW",
  DOWN: "KeyS",
  LEFT: "KeyA",
  RIGHT: "KeyD",
  A: "Numpad0",
  B: "NumpadDecimal",
  START: "NumpadEnter",
  SELECT: "ShiftRight",
  FAST_FORWARD: "Space",
};

/** Standard gamepad mapping (§7.2). */
const GAMEPAD_BUTTONS: [index: number, action: Action][] = [
  [0, "A"],
  [1, "B"],
  [8, "SELECT"],
  [9, "START"],
  [12, "UP"],
  [13, "DOWN"],
  [14, "LEFT"],
  [15, "RIGHT"],
  [7, "FAST_FORWARD"],
];
const STICK_THRESHOLD = 0.5;
const KEYMAP_STORAGE = "redchain.keymap";

export function loadKeymap(): KeyMap {
  try {
    const raw = localStorage.getItem(KEYMAP_STORAGE);
    if (raw) return { ...DEFAULT_KEYMAP, ...(JSON.parse(raw) as Partial<KeyMap>) };
  } catch {
    /* fall through to defaults */
  }
  return { ...DEFAULT_KEYMAP };
}

export function saveKeymap(map: KeyMap): void {
  localStorage.setItem(KEYMAP_STORAGE, JSON.stringify(map));
}

/**
 * Keyboard, Gamepad API and on-screen touch controls → one 8-bit mask, posted on
 * change. Fast-forward (hold) is separate: it changes wall-clock speed only.
 */
export class InputManager {
  private keymap: KeyMap = loadKeymap();
  private keyboard = 0;
  private gamepad = 0;
  private touch = 0;
  private ffKeyboard = false;
  private ffGamepad = false;
  private ffTouch = false;
  private lastMask = -1;
  private lastFf = false;
  private raf = 0;
  private attached = false;

  constructor(
    private readonly onMask: (mask: number) => void,
    private readonly onFastForward: (on: boolean) => void,
  ) {}

  attach(): void {
    if (this.attached) return;
    this.attached = true;
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    window.addEventListener("blur", this.releaseAll);
    this.raf = requestAnimationFrame(this.pollGamepads);
  }

  detach(): void {
    this.attached = false;
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    window.removeEventListener("blur", this.releaseAll);
    cancelAnimationFrame(this.raf);
    this.releaseAll();
  }

  setKeymap(map: KeyMap): void {
    this.keymap = map;
    saveKeymap(map);
  }

  getKeymap(): KeyMap {
    return { ...this.keymap };
  }

  /** Touch overlay buttons. */
  setTouch(action: Action, pressed: boolean): void {
    if (action === "FAST_FORWARD") this.ffTouch = pressed;
    else this.touch = pressed ? this.touch | Button[action] : this.touch & ~Button[action];
    this.publish();
  }

  private actionForCode(code: string): Action | null {
    for (const [action, c] of Object.entries(this.keymap) as [Action, string][]) if (c === code) return action;
    return null;
  }

  private isTyping(ev: KeyboardEvent): boolean {
    const t = ev.target as HTMLElement | null;
    return !!t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable);
  }

  private onKeyDown = (ev: KeyboardEvent): void => {
    if (this.isTyping(ev)) return;
    const action = this.actionForCode(ev.code);
    if (!action) return;
    ev.preventDefault();
    if (action === "FAST_FORWARD") this.ffKeyboard = true;
    else this.keyboard |= Button[action];
    this.publish();
  };

  private onKeyUp = (ev: KeyboardEvent): void => {
    const action = this.actionForCode(ev.code);
    if (!action) return;
    ev.preventDefault();
    if (action === "FAST_FORWARD") this.ffKeyboard = false;
    else this.keyboard &= ~Button[action];
    this.publish();
  };

  private releaseAll = (): void => {
    this.keyboard = 0;
    this.gamepad = 0;
    this.touch = 0;
    this.ffKeyboard = this.ffGamepad = this.ffTouch = false;
    this.publish();
  };

  private pollGamepads = (): void => {
    let mask = 0;
    let ff = false;
    const pads = navigator.getGamepads?.() ?? [];
    for (const pad of pads) {
      if (!pad || !pad.connected) continue;
      for (const [index, action] of GAMEPAD_BUTTONS) {
        const b = pad.buttons[index];
        const pressed = !!b && (b.pressed || b.value > 0.5);
        if (!pressed) continue;
        if (action === "FAST_FORWARD") ff = true;
        else mask |= Button[action];
      }
      const x = pad.axes[0] ?? 0;
      const y = pad.axes[1] ?? 0;
      if (x > STICK_THRESHOLD) mask |= Button.RIGHT;
      if (x < -STICK_THRESHOLD) mask |= Button.LEFT;
      if (y > STICK_THRESHOLD) mask |= Button.DOWN;
      if (y < -STICK_THRESHOLD) mask |= Button.UP;
    }
    if (mask !== this.gamepad || ff !== this.ffGamepad) {
      this.gamepad = mask;
      this.ffGamepad = ff;
      this.publish();
    }
    if (this.attached) this.raf = requestAnimationFrame(this.pollGamepads);
  };

  private publish(): void {
    const mask = (this.keyboard | this.gamepad | this.touch) & 0xff;
    if (mask !== this.lastMask) {
      this.lastMask = mask;
      this.onMask(mask);
    }
    const ff = this.ffKeyboard || this.ffGamepad || this.ffTouch;
    if (ff !== this.lastFf) {
      this.lastFf = ff;
      this.onFastForward(ff);
    }
  }
}
